import { useState, useEffect, useCallback } from 'react';
import { fashnai, TryOnRequest, TryOnResponse, FashnError } from '../lib/fashnai';
import { fileToBase64 } from '../lib/storage';

interface UseFashnTryOnReturn {
  loading: boolean;
  error: string | null;
  errorType: string | null;
  result: string | null;
  progress: number;
  tryOn: (modelImage: string, garmentImage: string) => Promise<void>;
  reset: () => void;
  clearResult: () => void;
}

export function useFashnTryOn(): UseFashnTryOnReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [tryOnId, setTryOnId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null);
  
  // Function to clean up polling
  const cleanupPolling = useCallback(() => {
    if (pollInterval) {
      clearInterval(pollInterval);
      setPollInterval(null);
    }
  }, [pollInterval]);
  
  // Reset the state
  const reset = useCallback(() => {
    // Immediately clear the result first
    setResult(null);
    
    // Then clear other state
    setLoading(false);
    setError(null);
    setErrorType(null);
    setTryOnId(null);
    setProgress(0);
    cleanupPolling();
  }, [cleanupPolling]);
  
  // Function to only clear the result (for immediately hiding the result UI)
  const clearResult = useCallback(() => {
    setResult(null);
  }, []);
  
  // Function to set error and error type
  const setErrorWithType = useCallback((message: string, type: string | null = null) => {
    setError(message);
    setErrorType(type);
    setLoading(false);
  }, []);
  
  // Start polling for results
  const startPolling = useCallback((id: string) => {
    // Clean up any existing polling
    cleanupPolling();
    
    // Start polling for status updates
    const interval = setInterval(async () => {
      try {
        const statusResponse = await fashnai.checkTryOnStatus(id);
        
        // Update progress based on status
        switch (statusResponse.status) {
          case 'starting':
            setProgress(10);
            break;
          case 'in_queue':
            setProgress(20);
            break;
          case 'processing':
            setProgress(50);
            break;
          case 'completed':
            setProgress(100);
            setResult(statusResponse.output?.[0] || null);
            setLoading(false);
            cleanupPolling();
            break;
          case 'failed':
            if (statusResponse.error) {
              setErrorWithType(
                fashnai.getReadableErrorMessage(statusResponse.error),
                statusResponse.error.name
              );
            } else {
              setErrorWithType('Try-on failed. Please try with different images.', 'PipelineError');
            }
            cleanupPolling();
            break;
        }
      } catch (err) {
        if (err instanceof Error) {
          setErrorWithType(err.message, 'APIError');
        } else {
          setErrorWithType('Failed to check try-on status', 'APIError');
        }
        cleanupPolling();
      }
    }, 2000); // Poll every 2 seconds
    
    setPollInterval(interval);
  }, [cleanupPolling, setErrorWithType]);
  
  // Start a new try-on
  const tryOn = async (modelImage: string, garmentImage: string) => {
    // Reset state first
    reset();
    
    setLoading(true);
    setError(null);
    setErrorType(null);
    setProgress(5);
    
    try {
      // Convert both images to base64 (with data URL prefix for the API)
      const modelImageBase64 = await fileToBase64(modelImage);
      const garmentImageBase64 = await fileToBase64(garmentImage);
      
      // Start the try-on process with base64-encoded images
      const request: TryOnRequest = {
        modelImage: `data:image/jpeg;base64,${modelImageBase64}`,
        garmentImage: `data:image/jpeg;base64,${garmentImageBase64}`,
        category: 'auto',
        segmentationFree: true,
        mode: 'quality'
      };
      
      const id = await fashnai.startTryOn(request);
      setTryOnId(id);
      setProgress(10);
      
      // Start polling for updates
      startPolling(id);
    } catch (err) {
      console.error('Try-on error:', err);
      
      if (err instanceof Error) {
        // Use our helper method to get a user-friendly message
        setErrorWithType(err.message, 'StartError');
      } else {
        setErrorWithType('Failed to start try-on. Please try again.', 'StartError');
      }
      
      setLoading(false);
    }
  };
  
  // Clean up polling when component unmounts
  useEffect(() => {
    return () => {
      cleanupPolling();
    };
  }, [cleanupPolling]);
  
  return {
    loading,
    error,
    errorType,
    result,
    progress,
    tryOn,
    reset,
    clearResult
  };
} 